import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Filter, ChevronDown, ExternalLink, Calendar } from "lucide-react";

import { getLeakTimeline } from "../../api/timeline";
import { fetchForums } from "../../api/status";
import { fetchSectors } from "../../api/leaks";
import type { TimelineDay } from "../../api/timeline";
import type { ForumStat, TimelineEntry } from "../../api/status";
import Skeleton from "../../components/Skeleton";
import { cleanTitle, getDisplayForum } from "../../utils/cleaners";

// ── Severity badge colours ────────────────────────────────────────────────────
const severityStyle = (s?: string | null) => {
  switch (s?.toLowerCase()) {
    case "critical":
      return { bg: "rgba(239,68,68,0.14)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.28)" };
    case "high":
      return { bg: "rgba(249,115,22,0.14)", color: "#f97316", border: "1px solid rgba(249,115,22,0.28)" };
    case "medium":
      return { bg: "rgba(251,191,36,0.14)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.28)" };
    case "low":
      return { bg: "rgba(34,197,94,0.14)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.28)" };
    default:
      return { bg: "var(--color-surface-2)", color: "var(--color-text-muted)", border: "1px solid var(--color-border)" };
  }
};

// ── Forum badge colours (deterministic) ───────────────────────────────────────
const FORUM_COLORS = [
  "#6366F1", "#3b82f6", "#6366F1", "#22c55e", "#a855f7",
  "#ec4899", "#f97316", "#fbbf24", "#ef4444", "#14b8a6",
];

function forumColor(forum: string) {
  let hash = 0;
  for (let i = 0; i < forum.length; i++) hash = forum.charCodeAt(i) + ((hash << 5) - hash);
  return FORUM_COLORS[Math.abs(hash) % FORUM_COLORS.length];
}

// ── Pill badge ────────────────────────────────────────────────────────────────
function Badge({ label, bg, color, border }: { label: string; bg: string; color: string; border: string }) {
  return (
    <span
      style={{
        display: "inline-block",
        background: bg,
        color,
        border,
        borderRadius: 999,
        padding: "3px 12px",
        fontSize: "0.72rem",
        fontWeight: 600,
        letterSpacing: "0.03em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ── Date range presets ────────────────────────────────────────────────────────
const DATE_PRESETS = [
  { label: "7 D", days: 7 },
  { label: "30 D", days: 30 },
  { label: "90 D", days: 90 },
] as const;

type FilterMode = "preset" | "custom";

// ── X-axis label step for each preset ─────────────────────────────────────────
function labelStep(days: number): number {
  if (days <= 7) return 1;
  if (days <= 30) return 3;
  return 7;
}

// ── Format a date string to a human-friendly label ────────────────────────────
const fmtDate = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtDateShort = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ── Main component ────────────────────────────────────────────────────────────
export default function TimelinePage() {
  // Chart data is derived from the same filtered response as the grouped list.
  const [chartData, setChartData] = useState<TimelineEntry[]>([]);

  // Full timeline data from /leaks/timeline (for the list view)
  const [timeline, setTimeline] = useState<TimelineDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [forumFilter, setForumFilter] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [forumTypeFilter, setForumTypeFilter] = useState("");
  const [forums, setForums] = useState<ForumStat[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);

  // Datewise custom range
  const [filterMode, setFilterMode] = useState<FilterMode>("preset");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Tooltip state
  const [tooltip, setTooltip] = useState<{ x: number; y: number; date: string; count: number; forums: string[] } | null>(null);

  // Load filter options once
  useEffect(() => {
    fetchForums().then(setForums).catch(console.error);
    fetchSectors().then(setSectors).catch(console.error);
  }, []);

  const uniqueForumsList = useMemo(() => {
    const result: { forum: string; label: string; type: string }[] = [];
    const seenLabels = new Set<string>();

    forums.forEach((f) => {
      const label = getDisplayForum(f.forum, null);
      const value = f.forum;
      const type = (f as any).forum_type || "database";

      if (label && !seenLabels.has(label) && label !== "Sector Intelligence") {
        seenLabels.add(label);
        result.push({ forum: value, label, type });
      }
    });

    if (forumTypeFilter) {
      const targetType = forumTypeFilter === "darknet" ? "database" : "ransomware";
      return result.filter((r) => r.type === targetType);
    }
    return result;
  }, [forums, forumTypeFilter]);

  // Load timeline list data from /leaks/timeline
  useEffect(() => {
    let cancelled = false;
    let isInitial = true;
    const load = async () => {
      await Promise.resolve();
      if (cancelled) return;
      if (isInitial) setLoading(true);

      const params: Record<string, string | number> = {};

      if (filterMode === "custom" && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else {
        params.days = days;
      }

      if (forumFilter) params.forum = forumFilter;
      if (sectorFilter) params.sector = sectorFilter;
      if (forumTypeFilter) params.forum_type = forumTypeFilter;

      try {
        const res = await getLeakTimeline(params);
        if (!cancelled) {
          setTimeline(res.timeline);
          setChartData(
            [...res.timeline]
              .sort((a, b) => a.date.localeCompare(b.date))
              .map((day) => ({
                date: day.date,
                count: day.count,
                forums: [...new Set(day.leaks.map((leak) => leak.forum).filter(Boolean))],
              }))
          );
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!cancelled && isInitial) {
          setLoading(false);
          setChartLoading(false);
        }
        isInitial = false;
      }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [days, forumFilter, sectorFilter, forumTypeFilter, filterMode, startDate, endDate]);

  // Chart metrics from zero-filled data
  const maxCount = useMemo(() => Math.max(...chartData.map((d) => d.count), 1), [chartData]);
  const totalLeaks = useMemo(() => chartData.reduce((s, d) => s + d.count, 0), [chartData]);
  const step = useMemo(() => labelStep(days), [days]);

  // Range label
  const rangeLabel = filterMode === "custom" && startDate && endDate
    ? `${fmtDate(startDate)} — ${fmtDate(endDate)}`
    : `last ${days} days`;

  if (loading && timeline.length === 0 && chartData.length === 0) return <Skeleton rows={8} className="mt-8" />;

  return (
    <div className="tl-page">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="tl-inner"
      >
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="tl-header">
          <div>
            <h1 className="tl-title">
              <CalendarDays size={26} style={{ marginRight: 10, opacity: 0.6 }} />
              Leak Timeline
            </h1>
            <p className="tl-subtitle">
              Daily breakdown of ingested leak intelligence
            </p>
          </div>
          <div className="tl-total-badge">
            <span className="tl-total-count">{totalLeaks.toLocaleString()}</span>
            &nbsp;leaks — {rangeLabel}
          </div>
        </div>

        {/* ── Filters ─────────────────────────────────────────────── */}
        <div className="tl-filters">
          <div className="tl-filters-left">
            <Filter size={14} style={{ color: "var(--color-text-muted)", flexShrink: 0 }} />

            {/* Date range toggles */}
            <div className="tl-range-group">
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.days}
                  className={`tl-range-btn${filterMode === "preset" && days === p.days ? " tl-range-btn--active" : ""}`}
                  onClick={() => {
                    setFilterMode("preset");
                    setDays(p.days);
                  }}
                >
                  {p.label}
                </button>
              ))}
              <button
                className={`tl-range-btn${filterMode === "custom" ? " tl-range-btn--active" : ""}`}
                onClick={() => setFilterMode("custom")}
                title="Custom date range"
              >
                <Calendar size={13} style={{ marginRight: 4 }} />
                Custom
              </button>
            </div>

            {/* Custom date inputs */}
            {filterMode === "custom" && (
              <div className="tl-date-inputs">
                <input
                  type="date"
                  className="tl-date-input"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  placeholder="Start date"
                />
                <span className="tl-date-separator">→</span>
                <input
                  type="date"
                  className="tl-date-input"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  placeholder="End date"
                />
              </div>
            )}

             {/* Forum Category dropdown */}
            <div className="tl-select-wrap">
              <select
                className={`tl-select${forumTypeFilter ? " tl-select--active" : ""}`}
                value={forumTypeFilter}
                onChange={(e) => {
                  setForumTypeFilter(e.target.value);
                  setForumFilter("");
                }}
              >
                <option value="">All Categories</option>
                <option value="darknet">Darknet Forums</option>
                <option value="ransomware">Ransomware Sites</option>
              </select>
              <ChevronDown className="tl-select-chevron" size={13} />
            </div>

            {/* Forum dropdown */}
            <div className="tl-select-wrap">
              <select
                className={`tl-select${forumFilter ? " tl-select--active" : ""}`}
                value={forumFilter}
                onChange={(e) => setForumFilter(e.target.value)}
              >
                <option value="">All Forums</option>
                {uniqueForumsList.map((f) => (
                  <option key={f.forum} value={f.forum}>
                    {f.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="tl-select-chevron" size={13} />
            </div>

            {/* Sector dropdown */}
            <div className="tl-select-wrap">
              <select
                className={`tl-select${sectorFilter ? " tl-select--active" : ""}`}
                value={sectorFilter}
                onChange={(e) => setSectorFilter(e.target.value)}
              >
                <option value="">All Sectors</option>
                {sectors.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <ChevronDown className="tl-select-chevron" size={13} />
            </div>
          </div>
        </div>

        {/* ── Bar Chart (from the filtered /leaks/timeline response) ────── */}
        <div className="tl-chart-card" style={{ position: "relative" }}>
          <div className="tl-chart-title">Daily Leak Volume</div>
          {chartLoading ? (
            <div className="tl-chart-loading">Loading…</div>
          ) : (
            <div className="tl-chart">
              {chartData.map((day, idx) => {
                const pct = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                const showLabel = idx % step === 0;
                return (
                  <div
                    key={day.date}
                    className="tl-bar-col"
                    onMouseEnter={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltip({
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                        date: day.date,
                        count: day.count,
                        forums: day.forums,
                      });
                    }}
                    onMouseLeave={() => setTooltip(null)}
                  >
                    <span className="tl-bar-count">{day.count > 0 ? day.count : ""}</span>
                    <div
                      className="tl-bar"
                      style={{ height: day.count > 0 ? `${Math.max(pct, 4)}%` : "0%" }}
                    />
                    <span className="tl-bar-label" style={{ visibility: showLabel ? "visible" : "hidden" }}>
                      {fmtDateShort(day.date)}
                    </span>
                  </div>
                );
              })}
              {chartData.length === 0 && (
                <div className="tl-empty">No data for the selected filters</div>
              )}
            </div>
          )}

          {/* Tooltip */}
          {tooltip && (
            <div
              style={{
                position: "fixed",
                left: tooltip.x,
                top: tooltip.y - 10,
                transform: "translate(-50%, -100%)",
                background: "#1e293b",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 8,
                padding: "8px 12px",
                zIndex: 100,
                pointerEvents: "none",
                minWidth: 140,
                boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
              }}
            >
              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: "white", marginBottom: 4 }}>
                {fmtDate(tooltip.date)}
              </div>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)" }}>
                {tooltip.count} leak{tooltip.count !== 1 ? "s" : ""}
              </div>
              {tooltip.forums.length > 0 && (
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)", marginTop: 4 }}>
                  {tooltip.forums.join(", ")}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Date-Grouped List ────────────────────────────────────── */}
        <div className="tl-list">
          {timeline.map((day) => (
            <div key={day.date} className="tl-day-group">
              <div className="tl-day-header">
                <span className="tl-day-date">{fmtDate(day.date)}</span>
                <span className="tl-day-count">
                  {day.count} leak{day.count !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="tl-day-cards">
                {day.leaks.map((leak) => {
                  const sev = severityStyle(leak.severity);
                  const fc = forumColor(leak.forum);
                  return (
                    <Link
                      to={`/leaks/${leak._id}`}
                      key={leak._id}
                      className="tl-leak-card"
                    >
                      <div className="tl-leak-top">
                        <span className="tl-leak-title">
                          {cleanTitle(leak.title) || "Untitled"}
                        </span>
                        <ExternalLink size={13} className="tl-leak-arrow" />
                      </div>
                      <div className="tl-leak-badges">
                        {(() => {
                          const isScrapedOnion = leak.forum.toLowerCase().endsWith(".onion") || leak.forum.toLowerCase().includes(".onion");
                          const mainBadgeLabel = getDisplayForum(leak.forum, leak.sectors);
                          
                          const extraSectors = leak.sectors
                            ? (isScrapedOnion ? leak.sectors.slice(1) : leak.sectors)
                            : [];

                          return (
                            <>
                              <Badge
                                label={mainBadgeLabel}
                                bg={`${fc}1a`}
                                color={fc}
                                border={`1px solid ${fc}40`}
                              />
                              {leak.severity && (
                                <Badge
                                  label={leak.severity}
                                  bg={sev.bg}
                                  color={sev.color}
                                  border={sev.border}
                                />
                              )}
                              {extraSectors.slice(0, 2).map((s) => (
                                <Badge
                                  key={s}
                                  label={s}
                                  bg="var(--color-surface-2)"
                                  color="var(--color-text-muted)"
                                  border="1px solid var(--color-border)"
                                />
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
